import { useFieldError } from "Shared/Components/Form/Helpers/getFormErrors";
import CustomFormLabel from "Shared/Components/Inputs/CustomFormLabel";
import CustomSelect from "Shared/Components/Inputs/CustomSelect";
import { Loading } from "Shared/Components/Loading/Loading";
import {
	Box,
	FormHelperText,
	ListSubheader,
	MenuItem,
	type SelectChangeEvent,
	type SxProps,
	type TextFieldVariants,
	type Theme,
	Typography,
	useTheme,
} from "@mui/material";
import type React from "react";
import type { ReactElement, ReactNode } from "react";
import type {
	Control,
	FieldError,
	FieldErrors,
	FieldPath,
	FieldValues,
} from "react-hook-form";
import { Controller, get } from "react-hook-form";

export interface OptionType {
	value: string | number;
	label: string;
	group?: string;
	optionLabel?: ReactElement;
	disabled?: boolean;
}

interface SelectProps<FormType extends FieldValues> {
	variant: TextFieldVariants;
	id: string;
	fullWidth?: boolean;
	multiple?: boolean;
	name: FieldPath<FormType>;
	label?: string;
	control: Control<FormType, object>;
	errors: FieldErrors;
	options: OptionType[];
	groupBy?: string;
	disabled?: boolean;
	AdditionalComponent?: ReactElement | null;
	numeric?: boolean;
	sx?: SxProps<Theme>;
	sxLabel?: SxProps<Theme>;
	size?: "small" | "medium";
	onChange?: (
		event: React.ChangeEventHandler<HTMLSelectElement> | unknown,
		value?: string | number | null | unknown,
	) => void;
	isLoading?: boolean;
}

const Select = <T extends object>(props: SelectProps<T>) => {
	const {
		name,
		control,
		label,
		options,
		id,
		errors,
		disabled,
		groupBy,
		AdditionalComponent,
		multiple,
		numeric,
		sx,
		isLoading,
		size,
		onChange: customOnChange,
		sxLabel,
		...rest
	} = props;
	const theme = useTheme();

	const { hasError, errorMessage } = useFieldError({
		fieldError: get(errors, name) as FieldError,
	});

	const groupedOptions = groupBy
		? options.reduce(
				(groups, option) => {
					const groupKey = option[groupBy] ?? "";

					if (!groups[groupKey]) {
						groups[groupKey] = [];
					}

					groups[groupKey].push(option);

					return groups;
				},
				{} as Record<string, OptionType[]>,
			)
		: null;

	return (
		<>
			<Controller
				control={control}
				name={name}
				// defaultValue={"" as PathValue<T, Path<T>> | undefined}
				render={({ field: { onChange, value, onBlur } }) => (
					<>
						{label && (
							<CustomFormLabel sx={sxLabel} htmlFor={id}>
								{label}
							</CustomFormLabel>
						)}
						<CustomSelect
							id={id}
							onChange={(event: SelectChangeEvent<unknown>, _: ReactNode) => {
								onChange(
									numeric ? Number(event.target.value) : event.target.value,
								);
								customOnChange?.(event, event.target.value);
							}}
							startAdornment={isLoading && <Loading />}
							value={
								multiple
									? Array.isArray(value)
										? (value as unknown[]).map((v) => String(v))
										: []
									: String(value ?? "")
							}
							name={name.toString()}
							onBlur={onBlur}
							error={!!hasError}
							disabled={disabled}
							multiple={multiple}
							sx={sx}
							size={size}
							{...rest}
						>
							{groupedOptions
								? Object.keys(groupedOptions).flatMap((groupName) => [
										<ListSubheader
											key={`subheader-${groupName}`}
											color="primary"
											sx={{
												backgroundColor: theme.palette.primary.light,
												padding: 1,
											}}
										>
											<Typography fontWeight={600}>{groupName}</Typography>
										</ListSubheader>,
										...groupedOptions[groupName].map((option) => (
											<MenuItem
												key={option.value}
												value={String(option.value)}
												disabled={option.disabled}
											>
												{option.label}
											</MenuItem>
										)),
									])
								: options.map((option) => (
										<MenuItem
											key={option.value}
											value={String(option.value)}
											disabled={option.disabled}
										>
											{option.label}
										</MenuItem>
									))}
						</CustomSelect>
						{errorMessage && (
							<FormHelperText sx={{ ml: "1rem", mt: "3px", mb: 2 }}>
								<Typography color="error" variant="caption">
									{errorMessage}
								</Typography>
							</FormHelperText>
						)}
						{AdditionalComponent && (
							<Box sx={{ mt: "3px", mb: 2 }}>{AdditionalComponent}</Box>
						)}
					</>
				)}
			/>
		</>
	);
};

export default Select;
